import { useState, useRef, useEffect, useCallback, useMemo, memo } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Modal,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
  Linking,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { Image } from "react-native";

import { useAuth } from "../../context/AuthContext";
import { sendMessage, subscribeToMessages } from "../../services/communityService";
import EmbedPreview from "./EmbedPreview";
import SharedBetCard from "./SharedBetCard";
import Appear from "../motion/Appear";
import { useNavigation } from "../../context/NavigationContext";
import { takePendingShare } from "../../utils/communityShare";
import { FL, FONT, display, label, body, nums, FLButton, ACCENT_GRADIENT } from "../floodlight";

// Floodlight (v1.71): the conversation inside the Community glass panel,
// split by hairlines. Header tools are round glass chips, the composer a glass
// field with a purple focus ring; a message brightens with a light-purple
// gutter under the pointer (CSS in CommunityScreen).

const WEB = Platform.OS === "web";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function initials(str = "") {
  const parts = str.replace(/\d+$/, "").match(/[A-Z][a-z]*/g) || [str];
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase() || str.slice(0, 2).toUpperCase();
}

function formatTime(createdAt) {
  if (!createdAt) return "";
  const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDate(createdAt) {
  if (!createdAt) return "";
  const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

function dayKey(createdAt) {
  if (!createdAt) return "";
  const d = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function minuteKey(createdAt) {
  if (!createdAt) return "";
  const d = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}-${d.getMinutes()}`;
}

// ─── URL helpers ─────────────────────────────────────────────────────────────

const URL_RE = /https?:\/\/[^\s<>"]+/g;

function parseTextSegments(text) {
  const segments = [];
  let lastIndex = 0;
  for (const match of text.matchAll(URL_RE)) {
    if (match.index > lastIndex)
      segments.push({ kind: "text", value: text.slice(lastIndex, match.index) });
    segments.push({ kind: "url", value: match[0] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length)
    segments.push({ kind: "text", value: text.slice(lastIndex) });
  return segments;
}
// ─── Day separator ────────────────────────────────────────────────────────────

const DaySeparator = memo(function DaySeparator({ createdAt }) {
  return (
    <View style={styles.daySep}>
      <Text style={styles.daySepText}>{formatDate(createdAt)}</Text>
      <View style={styles.daySepLine} />
    </View>
  );
});

// ─── Message row ─────────────────────────────────────────────────────────────

const MessageRow = memo(function MessageRow({ msg, showHeader, showTimestamp, isOwn, onRunQuery, index }) {
  const displayName = msg.username || msg.displayName || "Unknown";
  const abbr = initials(displayName);

  const segments = useMemo(() => parseTextSegments(msg.text ?? ""), [msg.text]);
  const embedUrl = useMemo(
    () => segments.find((s) => s.kind === "url")?.value ?? msg.videoUrl ?? null,
    [segments, msg.videoUrl],
  );

  return (
    <Appear index={index} distance={6} style={[styles.msgRow, showHeader && styles.msgRowFirst]} {...(WEB ? { dataSet: { msg: "" } } : {})}>
      {showHeader ? (
        msg.photoURL ? (
          <Image source={{ uri: msg.photoURL }} style={styles.msgAvatar} accessibilityLabel={`${displayName} avatar`} />
        ) : (
        <View style={[styles.msgAvatar, isOwn && styles.msgAvatarOwn]}>
          <Text style={[styles.msgAvatarText, isOwn && { color: FL.onGreen }]}>{abbr}</Text>
        </View>
        )
      ) : showTimestamp ? (
        <Text style={[styles.msgGutterTime, nums]}>{formatTime(msg.createdAt)}</Text>
      ) : (
        <View style={styles.msgGutter} />
      )}

      <View style={styles.msgContent}>
        {showHeader && (
          <View style={styles.msgHeader}>
            <Text style={styles.msgUsername}>{displayName}</Text>
            <Text style={[styles.msgTime, nums]}>{formatTime(msg.createdAt)}</Text>
          </View>
        )}
        {!!msg.text && <Text style={styles.msgText} selectable>
          {segments.map((seg, i) =>
            seg.kind === "url" ? (
              <Text
                key={i}
                style={styles.msgLink}
                onPress={() => Linking.openURL(seg.value).catch(() => {})}
              >
                {seg.value}
              </Text>
            ) : (
              <Text key={i}>{seg.value}</Text>
            ),
          )}
        </Text>}
        {!!embedUrl && <EmbedPreview url={embedUrl} />}
        {!!msg.bet && <SharedBetCard bet={msg.bet} onRun={onRunQuery} />}
        {msg.needsReview && isOwn && <Text style={styles.msgReview}>Visible to you · waiting for review</Text>}
      </View>
    </Appear>
  );
});

/** Round glass header tool: brightens under the pointer, purple wash when on. */
function Tool({ on, onPress, icon, accessibilityLabel, children }) {
  return (
    <Pressable
      onPress={onPress}
      style={[styles.tool, on && styles.toolOn]}
      dataSet={{ flChip: on ? "on" : "" }}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      aria-selected={on != null ? !!on : undefined}
    >
      <Ionicons name={icon} size={18} color={on ? FL.greenInk : FL.ink} />
      {children}
    </Pressable>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ChannelContent({ communityId, categoryId, channelId, channelName, channelTopic, compact, onOpenChannels, onOpenMembers, onlineCount }) {
  const { user } = useAuth();
  const { navigateTo } = useNavigation();
  const [pageSize, setPageSize] = useState(50);
  const [hasMore, setHasMore] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pendingBet, setPendingBet] = useState(() => takePendingShare());
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendNotice, setSendNotice] = useState(null);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const scrollRef = useRef(null);

  const resolvedName = channelName ?? channelId ?? "";
  const canSend = !!(communityId && categoryId && channelId);

  useEffect(() => {
    if (!communityId || !categoryId || !channelId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    const unsub = subscribeToMessages(communityId, categoryId, channelId, (msgs, meta) => {
      setMessages(msgs);
      setHasMore(!!meta?.hasMore);
      setLoading(false);
    }, pageSize, (err) => setLoadError(err));
    return unsub;
  }, [communityId, categoryId, channelId, pageSize]);

  useEffect(() => { setPageSize(50); setSearchText(""); }, [channelId]);

  const stickToBottom = useRef(true);
  useEffect(() => {
    if (stickToBottom.current) setTimeout(() => scrollRef.current?.scrollToEnd({ animated: false }), 50);
  }, [messages]);

  const runQuery = useCallback((q) => navigateTo("SgpFlow", { payload: { query: q } }), [navigateTo]);

  const handleAttachVideoUrl = useCallback(() => {
    setVideoUrl("");
    setVideoModalVisible(true);
  }, []);

  const handleConfirmVideoUrl = useCallback(() => {
    if (videoUrl.trim())
      setMessage((prev) => (prev ? `${prev} ${videoUrl.trim()}` : videoUrl.trim()));
    setVideoModalVisible(false);
  }, [videoUrl]);

  const handleSend = useCallback(async () => {
    if ((!message.trim() && !pendingBet) || !canSend || sending || !user) return;
    const text = message.trim();
    setSendError(null);
    setSendNotice(null);
    setMessage("");
    setSending(true);

    const result = await sendMessage(communityId, categoryId, channelId, {
      uid: user?.uid ?? "anonymous",
      username: user?.username ?? (user?.email ? user.email.split("@")[0] : null) ?? user?.displayName ?? null,
      displayName: user?.displayName ?? user?.username ?? null,
      photoURL: user?.photoURL ?? user?.profilePhoto ?? null,
      text,
      ...(pendingBet ? { bet: pendingBet } : {}),
    });

    if (result.blocked) {
      setMessage(text);
      setSendError("Message blocked: violates community guidelines.");
    } else if (result.error) {
      setSendError("Failed to send. Please try again.");
    } else {
      stickToBottom.current = true;
      setPendingBet(null);
      if (result.flagged) setSendNotice("Message sent and queued for review.");
    }
    setSending(false);
  }, [message, communityId, categoryId, channelId, user, canSend, sending, pendingBet]);

  // Build message list with day separators
  const visibleMessages = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return messages;
    return messages.filter((m) => [m.text, m.username, m.displayName, m.bet?.title, ...(m.bet?.legs || []).map((l) => l.label)].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [messages, searchText]);

  const renderedItems = useMemo(() => {
    const messages = visibleMessages;
    if (!messages.length) return [];
    const items = [];
    let lastDay = null;
    messages.forEach((msg, idx) => {
      const day = dayKey(msg.createdAt);
      if (day !== lastDay) {
        items.push({ type: "day", key: `day-${day}`, createdAt: msg.createdAt });
        lastDay = day;
      }
      const prev = messages[idx - 1];
      const newDay = idx === 0 || dayKey(prev?.createdAt) !== day;
      const showHeader = newDay || idx === 0 || prev?.uid !== msg.uid;
      const showTimestamp = !showHeader && minuteKey(prev?.createdAt) !== minuteKey(msg.createdAt);
      items.push({ type: "msg", key: msg.id, msg, showHeader, showTimestamp, isOwn: msg.uid === user?.uid });
    });
    return items;
  }, [visibleMessages, user?.uid]);

  const canSubmit = (message.trim() || pendingBet) && canSend && !sending && !!user;

  return (
    <View style={styles.root}>
      {/* Channel header */}
      <View style={styles.header}>
        <View style={[styles.headerLeft, { flex: 1, minWidth: 0 }]}>
          {compact && onOpenChannels ? (
            <Tool onPress={onOpenChannels} icon="menu" accessibilityLabel="Open channel list" />
          ) : null}
          <Text style={styles.headerHash} aria-hidden>#</Text>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.headerName} numberOfLines={1} accessibilityRole="header">{resolvedName}</Text>
            {!!channelTopic && !compact && <Text style={styles.headerTopic} numberOfLines={1}>{channelTopic}</Text>}
          </View>
        </View>
        <View style={styles.headerActions}>
          <Tool on={searchOpen} onPress={() => { setSearchOpen((o) => !o); if (searchOpen) setSearchText(""); }} icon={searchOpen ? "close" : "search-outline"} accessibilityLabel={searchOpen ? "Close search" : "Search this channel"} />
          {onOpenMembers ? (
            <Tool onPress={onOpenMembers} icon="people-outline" accessibilityLabel="Show members">
              {onlineCount > 0 && <View style={styles.headerDot} />}
            </Tool>
          ) : null}
        </View>
      </View>
      {searchOpen && (
        <View style={styles.searchWrap}>
          <View style={styles.searchBar} dataSet={{ flField: "" }}>
            <Ionicons name="search-outline" size={15} color={FL.ink2} />
            <TextInput value={searchText} onChangeText={setSearchText} placeholder={`Search #${resolvedName}`} placeholderTextColor={FL.muted} style={styles.searchInput} autoFocus accessibilityLabel="Search messages" />
            {!!searchText && <Text style={[styles.searchCount, nums]}>{visibleMessages.length} found</Text>}
          </View>
        </View>
      )}

      {/* Messages */}
      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator color={FL.ink} size="small" />
        </View>
      ) : (
        <ScrollView
          ref={scrollRef}
          style={styles.msgList}
          contentContainerStyle={styles.msgListInner}
          showsVerticalScrollIndicator={false}
          scrollEventThrottle={100}
          onScroll={(e) => {
            const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
            stickToBottom.current = contentSize.height - contentOffset.y - layoutMeasurement.height < 120;
          }}
          onContentSizeChange={() => { if (stickToBottom.current) scrollRef.current?.scrollToEnd({ animated: false }); }}
        >
          {hasMore && !searchText && (
            <FLButton
              label="Load earlier messages"
              variant="outline"
              size="sm"
              icon="arrow-up"
              onPress={() => { stickToBottom.current = false; setPageSize((n) => n + 50); }}
              style={styles.loadMore}
            />
          )}
          {loadError ? (
            <View style={styles.emptyCentered}>
              <Ionicons name="cloud-offline-outline" size={30} color={FL.ink} />
              <Text style={styles.emptyTitle}>Couldn't load #{resolvedName}</Text>
              <Text style={styles.emptyBody}>{user ? "Check your connection and try again." : "Sign in to read the conversation."}</Text>
            </View>
          ) : renderedItems.length === 0 ? (
            <View style={styles.emptyCentered}>
              <View style={styles.emptyIcon}>
                <Text style={styles.emptyHash}>#</Text>
              </View>
              <Text style={styles.emptyTitle}>{searchText ? "No messages match" : `Welcome to #${resolvedName}`}</Text>
              <Text style={styles.emptyBody}>{searchText ? "Try another word." : "This is the start of the channel. Share a bet from your bet slip or say hello."}</Text>
            </View>
          ) : (
            renderedItems.map((item) =>
              item.type === "day" ? (
                <DaySeparator key={item.key} createdAt={item.createdAt} />
              ) : (
                <MessageRow
                  key={item.key}
                  msg={item.msg}
                  showHeader={item.showHeader}
                  showTimestamp={item.showTimestamp}
                  isOwn={item.isOwn}
                  onRunQuery={runQuery}
                  index={0}
                />
              ),
            )
          )}
        </ScrollView>
      )}

      {/* Notices */}
      {!!sendError && (
        <View style={[styles.noticeBar, styles.noticeError]} accessibilityLiveRegion="polite">
          <Ionicons name="alert-circle-outline" size={14} color={FL.red} style={{ marginRight: 6 }} />
          <Text style={[styles.noticeText, { color: FL.red }]}>{sendError}</Text>
        </View>
      )}
      {!!sendNotice && (
        <View style={[styles.noticeBar, styles.noticeOk]} accessibilityLiveRegion="polite">
          <Ionicons name="checkmark-circle-outline" size={14} color={FL.greenInk} style={{ marginRight: 6 }} />
          <Text style={[styles.noticeText, { color: FL.greenInk }]}>{sendNotice}</Text>
        </View>
      )}

      {/* Chat input bar */}
      {!!pendingBet && (
        <View style={styles.pendingBet}>
          <View style={{ flex: 1 }}>
            <Text style={styles.pendingLabel}>Sharing</Text>
            <SharedBetCard bet={pendingBet} compact />
          </View>
          <Tool onPress={() => setPendingBet(null)} icon="close" accessibilityLabel="Remove the shared bet" />
        </View>
      )}
      {!user ? (
        <View style={styles.signInBar}>
          <Text style={styles.signInText}>Sign in to join the conversation and share bets.</Text>
          <FLButton label="Sign in" variant="green" size="sm" onPress={() => navigateTo("Login", { redirectAfterLogin: "Community" })} />
        </View>
      ) : (
      <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined}>
        <View style={styles.chatBar}>

          {/* Input area */}
          <View style={styles.inputWrap} dataSet={{ flField: "" }}>
            <TextInput
              style={styles.textInput}
              value={message}
              onChangeText={(val) => {
                setMessage(val);
                if (sendError) setSendError(null);
                if (sendNotice) setSendNotice(null);
              }}
              placeholder={pendingBet ? "Add a note (optional)" : `Message #${resolvedName}`}
              accessibilityLabel={`Message #${resolvedName}`}
              placeholderTextColor={FL.muted}
              multiline
              maxLength={2000}
              onKeyPress={Platform.OS === "web" ? (e) => { if (e.nativeEvent.key === "Enter" && !e.nativeEvent.shiftKey) { e.preventDefault?.(); handleSend(); } } : undefined}
            />
            <View style={styles.inputActions}>
              <TouchableOpacity onPress={handleAttachVideoUrl} hitSlop={8} style={styles.inputActionBtn} accessibilityRole="button" accessibilityLabel="Attach a video link">
                <Ionicons name="play-circle-outline" size={20} color={FL.ink2} />
              </TouchableOpacity>
            </View>
          </View>

          <FLButton
            label="Send"
            variant="green"
            size="sm"
            icon="send"
            onPress={handleSend}
            disabled={!canSubmit}
            busy={sending}
            accessibilityLabel="Send message"
            style={styles.sendBtn}
          />
        </View>
      </KeyboardAvoidingView>
      )}

      {/* Video URL modal */}
      <Modal
        visible={videoModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setVideoModalVisible(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setVideoModalVisible(false)}
        >
          <View style={styles.modalCard} onStartShouldSetResponder={() => true}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle} accessibilityRole="header">Embed video URL</Text>
              <TouchableOpacity onPress={() => setVideoModalVisible(false)} hitSlop={8} accessibilityRole="button" accessibilityLabel="Close">
                <Ionicons name="close" size={20} color={FL.ink} />
              </TouchableOpacity>
            </View>
            <Text style={styles.modalHint}>Paste a YouTube, Twitch, or other video link</Text>
            <View style={styles.modalField} dataSet={{ flField: "" }}>
              <TextInput
                style={styles.modalInput}
                value={videoUrl}
                onChangeText={setVideoUrl}
                placeholder="https://..."
                placeholderTextColor={FL.muted}
                autoFocus
                autoCapitalize="none"
                keyboardType="url"
                accessibilityLabel="Video link"
              />
            </View>
            <View style={styles.modalFooter}>
              <FLButton label="Cancel" variant="outline" size="sm" arrow={false} onPress={() => setVideoModalVisible(false)} />
              <FLButton label="Attach" variant="green" size="sm" onPress={handleConfirmVideoUrl} disabled={!videoUrl.trim()} />
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const noOutline = WEB ? { outlineStyle: "none" } : {};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    minWidth: 0,
    backgroundColor: "transparent",
    overflow: "hidden",
    flexDirection: "column",
  },

  // ── Header ────────────────────────────────────────────────────────
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    paddingHorizontal: 16,
    height: 60,
    borderBottomWidth: 1,
    borderBottomColor: FL.hair,
  },
  headerLeft: { flexDirection: "row", alignItems: "center", gap: 10 },
  headerHash: { ...display(24, { width: 100 }), color: FL.greenInk, lineHeight: 28 },
  headerName: { ...display(18, { width: 104, lineHeight: 1.1 }), letterSpacing: -0.2 },
  headerTopic: { ...body(12), lineHeight: 16, color: FL.muted, marginTop: 2 },
  headerActions: { flexDirection: "row", alignItems: "center", gap: 6 },
  tool: {
    width: 40,
    height: 40,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: FL.hair2,
    backgroundColor: FL.card,
  },
  toolOn: { backgroundColor: FL.greenWash, borderColor: "rgba(162,89,255,0.45)" },
  headerDot: { position: "absolute", top: 4, right: 4, width: 8, height: 8, borderRadius: 999, backgroundColor: FL.pos },

  searchWrap: { paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: FL.hair },
  searchBar: { flexDirection: "row", alignItems: "center", gap: 8, paddingHorizontal: 14, height: 44, borderRadius: 999, borderWidth: 1, borderColor: FL.hair2, backgroundColor: FL.paper2 },
  searchInput: { flex: 1, minWidth: 0, color: FL.ink, fontFamily: FONT, fontSize: 14, paddingVertical: 6, ...noOutline },
  searchCount: { ...label(10), color: FL.ink2 },

  loadMore: { alignSelf: "center", marginVertical: 12 },

  // ── Messages ──────────────────────────────────────────────────────
  msgList: { flex: 1 },
  msgListInner: { paddingHorizontal: 12, paddingTop: 8, paddingBottom: 12 },
  centered: { flex: 1, alignItems: "center", justifyContent: "center" },

  emptyCentered: { flex: 1, alignItems: "center", paddingTop: 56, paddingHorizontal: 24 },
  emptyIcon: { width: 72, height: 72, borderRadius: 22, backgroundColor: FL.green, ...(WEB ? { backgroundImage: ACCENT_GRADIENT, boxShadow: "0 12px 40px rgba(124,58,237,0.35)" } : {}), alignItems: "center", justifyContent: "center", marginBottom: 20 },
  emptyHash: { ...display(40, { width: 100 }), lineHeight: 44, color: FL.onGreen },
  emptyTitle: { ...display(22, { width: 104, lineHeight: 1.1 }), letterSpacing: -0.3, textAlign: "center", marginBottom: 10, marginTop: 6 },
  emptyBody: { ...body(14), color: FL.ink2, textAlign: "center", maxWidth: 420 },

  daySep: { flexDirection: "row", alignItems: "center", marginTop: 20, marginBottom: 6, gap: 10, paddingHorizontal: 4 },
  daySepLine: { flex: 1, height: 1, backgroundColor: FL.hair },
  daySepText: { ...label(10), color: FL.muted, fontWeight: "700", letterSpacing: 1.4 },

  msgRow: { flexDirection: "row", alignItems: "flex-start", paddingVertical: 3, paddingHorizontal: 8 },
  msgRowFirst: { marginTop: 12, paddingTop: 4 },
  msgAvatar: { width: 36, height: 36, borderRadius: 999, alignItems: "center", justifyContent: "center", marginRight: 12, flexShrink: 0, backgroundColor: FL.stage, borderWidth: 1, borderColor: FL.hair2 },
  msgAvatarOwn: { backgroundColor: FL.green, borderColor: "transparent", ...(WEB ? { backgroundImage: ACCENT_GRADIENT } : {}) },
  msgAvatarText: { color: FL.onStage, fontFamily: FONT, fontSize: 13, fontWeight: "800", letterSpacing: 0.4 },
  msgGutter: { width: 36, marginRight: 12, flexShrink: 0 },
  msgGutterTime: { width: 36, marginRight: 12, flexShrink: 0, fontFamily: FONT, fontSize: 9.5, color: FL.muted, textAlign: "center", alignSelf: "center", lineHeight: 14 },
  msgContent: { flex: 1, minWidth: 0 },
  msgHeader: { flexDirection: "row", alignItems: "baseline", gap: 8, marginBottom: 2 },
  msgUsername: { fontFamily: FONT, fontSize: 14, fontWeight: "700", color: FL.ink },
  msgTime: { ...label(10) },
  msgText: { fontFamily: FONT, fontSize: 14.5, color: FL.ink, lineHeight: 21 },
  msgLink: { color: FL.blueInk, fontWeight: "600", textDecorationLine: "underline" },
  msgReview: { ...label(10), color: FL.amber, marginTop: 6 },

  // ── Notices ───────────────────────────────────────────────────────
  noticeBar: { flexDirection: "row", alignItems: "center", paddingHorizontal: 16, paddingVertical: 8, borderTopWidth: 1 },
  noticeError: { backgroundColor: FL.redWash, borderTopColor: FL.red },
  noticeOk: { backgroundColor: FL.greenWash, borderTopColor: FL.greenInk },
  noticeText: { fontFamily: FONT, fontSize: 13, fontWeight: "600" },

  // ── Composer ──────────────────────────────────────────────────────
  pendingBet: { flexDirection: "row", alignItems: "flex-start", gap: 10, paddingHorizontal: 16, paddingTop: 12, borderTopWidth: 1, borderTopColor: FL.hair },
  pendingLabel: { ...label(10), color: FL.greenInk, fontWeight: "800" },
  signInBar: { flexDirection: "row", alignItems: "center", gap: 12, padding: 14, borderTopWidth: 1, borderTopColor: FL.hair, flexWrap: "wrap" },
  signInText: { ...body(14), flex: 1, minWidth: 180, color: FL.ink2 },
  chatBar: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: FL.hair,
  },
  inputWrap: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "flex-end",
    paddingLeft: 14,
    paddingRight: 8,
    minHeight: 44,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: FL.hair2,
    backgroundColor: FL.paper2,
  },
  textInput: { flex: 1, minWidth: 0, fontFamily: FONT, fontSize: 15, color: FL.ink, paddingVertical: 10, maxHeight: 120, ...noOutline },
  inputActions: { flexDirection: "row", alignItems: "center", paddingBottom: 9, gap: 2 },
  inputActionBtn: { padding: 3, ...(WEB ? { cursor: "pointer" } : {}) },
  sendBtn: { minHeight: 44 },

  // ── Video modal ───────────────────────────────────────────────────
  modalOverlay: { flex: 1, backgroundColor: "rgba(0,0,0,0.6)", justifyContent: "center", alignItems: "center", padding: 16, ...(WEB ? { backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" } : {}) },
  modalCard: {
    width: "100%",
    maxWidth: 440,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: FL.hair2,
    padding: 22,
    backgroundColor: FL.paper,
    ...(WEB ? { backgroundImage: "linear-gradient(rgba(255,255,255,0.07), rgba(255,255,255,0.07))", boxShadow: "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(162,89,255,0.18)" } : {}),
  },
  modalHeader: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  modalTitle: { ...display(20, { width: 104, lineHeight: 1.1 }), letterSpacing: -0.3 },
  modalHint: { ...body(14), color: FL.ink2, marginBottom: 14 },
  modalField: { borderRadius: 14, borderWidth: 1, borderColor: FL.hair2, backgroundColor: FL.paper2, paddingHorizontal: 14 },
  modalInput: { paddingVertical: 11, color: FL.ink, fontFamily: FONT, fontSize: 15, ...noOutline },
  modalFooter: { flexDirection: "row", justifyContent: "flex-end", flexWrap: "wrap", gap: 10, marginTop: 18 },
});
